/**
 * Application route constants.
 *
 * Always import from here -- never hardcode path strings in components.
 * Functions (e.g. DETAIL) generate parameterised paths.
 */
export const ROUTES = {
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/signup',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    ACCEPT_INVITATION: '/accept-invitation',
  },

  DASHBOARD: '/dashboard',

  /** CAPTURE_AGENT only — stripped-down mobile visitor capture interface */
  CAPTURE: '/capture',

  /** Payment onboarding routes — accessible without authentication */
  PAYMENT: {
    PROCESSING: (reference: string) => `/payment/processing/${reference}`,
  },

  /** Org awaiting admin review after successful payment */
  PENDING_APPROVAL: '/pending-approval',

  VISITORS: {
    LIST:   '/visitors',
    REGISTER: '/visitors/register',
    DETAIL: (id: string) => `/visitors/${id}`,
  },

  SMS: {
    LIST:            '/sms',
    TEMPLATES:       '/sms/templates',
    CAMPAIGNS:       '/sms/campaigns',
    CAMPAIGN_DETAIL: (id: string) => `/sms/campaigns/${id}`,
    CREDITS:         '/sms/credits',
  },

  COMPLIANCE: {
    OVERVIEW: '/compliance',
    EVENTS:   '/compliance/events',
    RULES:    '/compliance/rules',
  },

  ANALYTICS: {
    OVERVIEW: '/analytics',
    EXPORTS:  '/analytics/exports',
  },
  AUDIT_LOGS: '/audit-logs',
  SEARCH: '/search',

  SETTINGS: {
    ROOT:             '/settings',
    ORGANIZATION:     '/settings/organization',
    MEMBERS:          '/settings/members',
    SUBSCRIPTION:     '/settings/subscription',
    BILLING:          '/settings/billing',
    BILLING_PAY:      (type: string, id: string) => `/settings/billing/pay/${type}/${id}`,
    ACCOUNT:          '/settings/account',
    PROFILE:          '/settings/profile',
    VISITOR_PURPOSES: '/settings/visitor-purposes',
    WORKFLOWS:        '/settings/workflows',
    WORKFLOW_DETAIL:  (purposeId: string) => `/settings/workflows/${purposeId}`,
    KIOSK:            '/settings/kiosk',
    // Dynamic Field Registry (V1.5)
    FIELD_CONFIG:     '/settings/field-config',
  },

  /** Public tablet self-registration interface (no auth required) */
  KIOSK: (token: string) => `/kiosk/${token}`,

  /** SUPER_ADMIN only -- platform-wide management routes */
  PRE_REGISTRATIONS: {
    LIST:   '/pre-registrations',
    DETAIL: (id: string) => `/pre-registrations/${id}`,
  },

  /** Public (no auth) — visitor self-service link */
  PRE_REGISTER: (token: string) => `/pre-register/${token}`,

  PLATFORM_ADMIN: {
    DASHBOARD:     '/platform',
    ORGANIZATIONS: '/platform/organizations',
    ORG_DETAIL:    (id: string) => `/platform/organizations/${id}`,
    PLANS:         '/platform/plans',
    REQUESTS:      '/platform/requests',
    // SMS management (cross-tenant)
    SMS_TEMPLATES:    '/platform/sms/templates',
    SMS_MESSAGES:     '/platform/sms/messages',
    SMS_CAMPAIGNS:    '/platform/sms/campaigns',
    SMS_CREDITS:      '/platform/sms/credits',
    SMS_TRANSACTIONS: '/platform/sms/transactions',
    // Auth management (cross-tenant)
    USERS:                '/platform/auth/users',
    USER_DETAIL:          (id: string) => `/platform/auth/users/${id}`,
    MEMBERSHIPS:          '/platform/auth/memberships',
    PASSWORD_RESET_TOKENS: '/platform/auth/password-reset-tokens',
    REFRESH_TOKENS:       '/platform/auth/refresh-tokens',
    // Kiosk management (cross-tenant)
    KIOSK_SESSIONS:       '/platform/kiosk/sessions',
    KIOSK_SESSION_DETAIL: (id: string) => `/platform/kiosk/sessions/${id}`,
    // Organisation Invitations
    INVITATIONS:          '/platform/organizations/invitations',
    // Organisation Settings
    ORG_SETTINGS:         '/platform/organizations/settings',
    // Organisation Payments
    ORG_PAYMENTS:         '/platform/payments',
    // Subscriptions
    SUBSCRIPTIONS:        '/platform/subscriptions',
    SUBSCRIPTION_DETAIL:  (id: string) => `/platform/subscriptions/${id}`,
    // Visitor data (cross-tenant)
    VISITOR_PURPOSES_ADMIN: '/platform/visitor-purposes',
    VISIT_LOGS:             '/platform/visit-logs',
    VISITORS_ADMIN:         '/platform/visitors',
    VISITOR_ADMIN_DETAIL:   (id: string) => `/platform/visitors/${id}`,
  },
} as const
